<template>
  <div style="position: relative;">
    <!-- Marriages -->
    <div>
      <MarriagesCard
        v-for="marriage of peSortedCharacteristic('spouses')"
        :key="marriage.id"
        :item="marriage"
        class="mb-4"
      />
    </div>

    <div v-if="peSurveysValidationActive">
      <MarriagesCard
        v-for="item of partySurveyCardsByIdentifiers('marriages-card-')"
        :key="item.identifier"
        :item="item.inputs"
        :survey-identifier="item.identifier"
        class="mb-4"
        survey-validation
      />
    </div>

    <SectionAdderLine
      section="Marriage"
      :has-new-section="peMarriagesCardHasNewSection"
      @on-add="peMarriagesCardAddSection"
    />

    <!-- Children -->
    <div>
      <ChildrenCard
        v-for="child of peSortedCharacteristic('children')"
        :key="child.id"
        :item="child"
        class="mb-4"
      />
    </div>

    <div v-if="peSurveysValidationActive">
      <ChildrenCard
        v-for="item of partySurveyCardsByIdentifiers('children-card-')"
        :key="item.identifier"
        :item="item.inputs"
        :survey-identifier="item.identifier"
        class="mb-4"
        survey-validation
      />
    </div>

    <SectionAdderLine
      section="Child"
      :has-new-section="peChildrenCardHasNewSection"
      @on-add="peChildrenCardAddSection"
    />

    <!-- Parents -->
    <div>
      <ParentsCard
        v-for="parent of peSortedCharacteristic('parents')"
        :key="parent.id"
        :item="parent"
        class="mb-4"
      />
    </div>

    <div v-if="peSurveysValidationActive">
      <ParentsCard
        v-for="item of partySurveyCardsByIdentifiers('parents-card-')"
        :key="item.identifier"
        :item="item.inputs"
        :survey-identifier="item.identifier"
        class="mb-4"
        survey-validation
      />
    </div>

    <SectionAdderLine
      section="Parent"
      :has-new-section="peParentsCardHasNewSection"
      @on-add="peParentsCardAddSection"
    />

    <!-- Parents -->
    <div>
      <SiblingsCard
        v-for="sibling of peSortedCharacteristic('siblings')"
        :key="sibling.id"
        :item="sibling"
        class="mb-4"
      />
    </div>

    <div v-if="peSurveysValidationActive">
      <SiblingsCard
        v-for="item of partySurveyCardsByIdentifiers('siblings-card-')"
        :key="item.identifier"
        :item="item.inputs"
        :survey-identifier="item.identifier"
        class="mb-4"
        survey-validation
      />
    </div>

    <SectionAdderLine
      section="Sibling"
      :has-new-section="peSiblingsCardHasNewSection"
      @on-add="peSiblingsCardAddSection"
    />

    <v-overlay
      absolute
      :value="!canView"
      :class="{'party-overlay': !canView}"
      class="align-start"
    >
      <v-row
        align="start"
        justify="center"
      >
        <v-col
          cols="12"
          class="text-center"
        >
          <h2>You do not have permissions to view this section.</h2>
        </v-col>
      </v-row>
    </v-overlay>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import Actions from '@/data/Permissions/enums.json'
import { alertHandler } from '@/alertHandler'

import { PARTY_NAMESPACE } from '@/store/party'
import { USER_NAMESPACE } from '@/store/user'

import ChildrenCard from './SectionCards/FamiliesCards/ChildrenCard'
import MarriagesCard from './SectionCards/FamiliesCards/MarriagesCard'
import ParentsCard from './SectionCards/FamiliesCards/ParentsCard'
import SectionAdderLine from './SectionTools/SectionAdderLine'
import SiblingsCard from './SectionCards/FamiliesCards/SiblingsCard'

export default {
  name: 'PEFamilies',

  components: {
    ChildrenCard,
    MarriagesCard,
    ParentsCard,
    SectionAdderLine,
    SiblingsCard,
  },

  beforeRouteLeave (_, __, next) {
    if (this.peAnyUnlockedSections) {
      const answer = window.confirm('Do you really want to leave? you have unsaved changes!')
      if (answer) {
        this.peLockPartySections()
        next()
      } else next(false)
    } else {
      this.peLockPartySections()
      next()
    }
  },

  props: {
    id: {
      type: String || Number,
      required: true,
    },
  },

  setup () {
    return { ...alertHandler() }
  },

  data () {
    return {
      canView: true,
    }
  },

  computed: {
    userCan () {
      return {
        viewSection: this.can(Actions.Platform.PartyEdit.Sections.Families.View),
      }
    },

    ...mapGetters(PARTY_NAMESPACE, [
      'peAnyUnlockedSections',
      'peChildrenCardHasNewSection',
      'peMarriagesCardHasNewSection',
      'peParentsCardHasNewSection',
      'peSiblingsCardHasNewSection',
      'peSortedCharacteristic',
    ]),

    ...mapGetters(PARTY_NAMESPACE, [
      'partySurveyCardsByIdentifiers',
      'peSurveysValidationActive',
    ]),

    ...mapGetters(USER_NAMESPACE, [
      'can',
    ]),
  },

  created () {
    if (!this.userCan.viewSection) {
      this.canView = false
      this.displayAlert('error', 'You do not have permissions to view this section.')
      return
    }

    this.peGetSelectedPartySection({ id: this.id, section: 'families' }).
      catch(e => {
        if (e.status === 403) this.$router.push({ name: 'resourceAccessDenied', query: { party_id: this.id } })
        else if (e.status === 404) this.$router.push({ name: 'resourceNotFound' })
        else this.displayAlert('error', `An unexpected error occurred: ${e.message}`)
      })
  },

  methods: {
    ...mapActions(PARTY_NAMESPACE, [
      'peChildrenCardAddSection',
      'peGetSelectedPartySection',
      'peLockPartySections',
      'peMarriagesCardAddSection',
      'peParentsCardAddSection',
      'peSiblingsCardAddSection',
    ]),
  },
}
</script>

<style scoped>
.party-overlay {
  margin-top: -75px;
  padding-top: 50vh;
  z-index: 10;
  backdrop-filter: blur(6px);
}
</style>
